import type { JSX, Signal } from '@builder.io/qwik';
import { component$, useContext, useSignal } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import { AppDataContext } from '~/contexts/AppDataContext';
import MobileNavItem from '~/components/Layout/Header/Mobile/MobileNavItem';
import MobileDivider from '~/components/Layout/Header/Mobile/MobileDivider';
import SocialIcons from '~/components/Shared/Social/SocialIcons';
import {
  getCSSColorBg,
  getCSSColorText,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { getHeaderHeight } from '~/utilities/getHeaderHeight';

const mobileNavWrapClasses = cva(
  'fixed block lg:hidden w-full ease-in-out duration-300 z-[2] overflow-y-auto',
  {
    variants: {
      active: {
        true: 'opacity-1 left-0',
        false: 'opacity-0 -left-full',
      },
      background: getCSSColorBg(),
    },
  }
);

const socialTitleClasses = cva('mb-4', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
  },
});

type MobileNavType = {
  mobileActive: Signal<boolean>;
  headerHeightOverwrite?: string;
};

export default component$(
  ({ mobileActive, headerHeightOverwrite }: MobileNavType): JSX.Element => {
    const appData = useContext(AppDataContext);
    const activeItem = useSignal('');

    const headerHeight = headerHeightOverwrite
      ? headerHeightOverwrite
      : getHeaderHeight(appData.themeComponents, appData.payloadToSSR.layout, {
          screenSize: 'Small',
          include: ['firstRow'],
        });

    return (
      <div
        style={{ top: headerHeight, height: `calc(100vh - ${headerHeight})` }}
        class={mobileNavWrapClasses({
          active: mobileActive.value,
          background:
            appData.themeComponents.header.hamburgerMenuLinksBackgroundColor
              .value,
        })}
      >
        <nav>
          <ul class='m-5'>
            {appData.payloadToSSR.layout.menu.links?.value.map(
              (each, index) => (
                <MobileNavItem
                  key={index}
                  item={each}
                  activeItem={activeItem}
                  level={0}
                />
              )
            )}
          </ul>
        </nav>
        {appData.payloadToSSR.layout.menu.socialLinks && (
          <>
            <MobileDivider />
            <div class='mt-10 mr-4 mb-4 ml-6'>
              <SocialIcons
                icons={appData.payloadToSSR.layout.menu.socialLinks.value}
                title={appData.payloadToSSR.layout.menu.socialLinksTitle?.value}
                titleClasses={socialTitleClasses({
                  color:
                    appData.themeComponents.header
                      .hamburgerMenuSocialLinksTitleColor.value,
                  ...getFontSizeObject(
                    appData.themeComponents.header,
                    'hamburgerMenuSocialLinksTitleFontSize'
                  ),
                })}
                color={
                  appData.themeComponents.header.hamburgerMenuSocialLinksColor
                    .value
                }
                hoverColor={
                  appData.themeComponents.header
                    .hamburgerMenuSocialLinksHoverColor.value
                }
              />
            </div>
          </>
        )}
      </div>
    );
  }
);
